import { lazy, Suspense } from "react";
import { Link } from "@/lib/router-compat";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import { usePageContent } from "@/hooks/usePageContent";
import { PageContentProvider } from "@/contexts/PageContentContext";
import FAQSection, { buildFaqJsonLd } from "@/components/FAQSection";
import { FAQS } from "@/data/faqs";

// Lazy-load below-the-fold sections
const AllInOneSection = lazy(() => import("@/components/AllInOneSection"));
const PrescriptionSection = lazy(() => import("@/components/PrescriptionSection"));
const NetworkStats = lazy(() => import("@/components/NetworkStats"));
const WhyPartnerSection = lazy(() => import("@/components/WhyPartnerSection"));
const AIOpticianSection = lazy(() => import("@/components/AIOpticianSection"));
const VersatilitySection = lazy(() => import("@/components/VersatilitySection"));
const StartSellingSection = lazy(() => import("@/components/StartSellingSection"));
const TopBrands = lazy(() => import("@/components/TopBrands"));
const RevolutionizeSection = lazy(() => import("@/components/RevolutionizeSection"));
const ResellerBenefits = lazy(() => import("@/components/ResellerBenefits"));
const BlogPreview = lazy(() => import("@/components/BlogPreview"));
const ContactForm = lazy(() => import("@/components/ContactForm"));
const Footer = lazy(() => import("@/components/Footer"));

// JSON-LD for Organization, WebSite, Service, and SoftwareApplication is emitted
// by the inline crawler script in index.html (single source of truth for all
// structured data, OG, and meta tags). Do not duplicate it here via <SEO />.

const Index = () => {
  const { t } = usePageContent("home");

  return (
    <PageContentProvider value={t}>
      <div className="min-h-screen bg-background">
        <Navbar />
        <main>
          {/* What is GoHub?, AEO featured snippet target */}
          <section className="sr-only" aria-label="What is GoHub">
            <h2>What is GoHub?</h2>
            <p>
              GoHub is a B2B SaaS platform that enables online retailers, pharmacies, and eyewear brands to sell prescription eyewear through their own stores. It provides AI-guided prescription entry, virtual try-on, PD measurement, lens configuration, and fulfillment through 12 certified optical labs in the EU and US. Retailers integrate once and gain access to over 100 brands and 17,000+ SKUs with no inventory, no optical expertise, and no compliance burden.
            </p>
          </section>
          <HeroSection />
          <Suspense fallback={<div className="min-h-[200px]" />}>
            <AllInOneSection />
            <NetworkStats />
            <PrescriptionSection />
            <WhyPartnerSection />
            <AIOpticianSection />
            <VersatilitySection />
            <StartSellingSection />
            <TopBrands />
            <RevolutionizeSection />
            <ResellerBenefits />
            <section className="bg-secondary/40 py-12" aria-labelledby="explore-solutions">
              <div className="container mx-auto text-center">
                <h2 id="explore-solutions" className="mb-4 text-2xl font-bold text-foreground md:text-3xl">
                  Explore GoHub solutions
                </h2>
                <p className="mx-auto mb-6 max-w-2xl text-muted-foreground">
                  Built for online pharmacies, eyewear brands, and resellers, with virtual try-on
                  and PD measurement included.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3 text-sm font-medium">
                  <Link
                    to="/virtual-try-on"
                    className="rounded-full border border-border bg-card px-5 py-2 text-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    Virtual Try-On & PD measurement
                  </Link>
                  <Link
                    to="/pharmacy"
                    className="rounded-full border border-border bg-card px-5 py-2 text-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    For online pharmacies
                  </Link>
                  <Link
                    to="/brands-resellers"
                    className="rounded-full border border-border bg-card px-5 py-2 text-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    For brands & resellers
                  </Link>
                  <Link
                    to="/opticians"
                    className="rounded-full border border-border bg-card px-5 py-2 text-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    For opticians
                  </Link>
                </div>
              </div>
            </section>
            <section className="py-16 md:py-20" aria-labelledby="how-it-works">
              <div className="container mx-auto max-w-4xl px-4">
                <h2 id="how-it-works" className="mb-4 text-center text-2xl font-bold text-foreground md:text-3xl">
                  How GoHub works
                </h2>
                <p className="mx-auto mb-10 max-w-2xl text-center text-muted-foreground">
                  Add prescription eyewear to your store in four steps. No optical staff, no inventory, no lab contracts.
                </p>
                <ol className="grid gap-6 md:grid-cols-2">
                  <li>
                    <h3 className="mb-1 text-base font-semibold text-foreground">1. Integrate</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Plug GoHub into Shopify, WooCommerce, Magento, PrestaShop, or any custom platform via JavaScript embed or API. Most stores are live within days.
                    </p>
                  </li>
                  <li>
                    <h3 className="mb-1 text-base font-semibold text-foreground">2. Customers shop with AI guidance</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Shoppers browse 17,000+ frames, try them on with virtual try-on, measure their pupillary distance via webcam, and enter their prescription with AI help.
                    </p>
                  </li>
                  <li>
                    <h3 className="mb-1 text-base font-semibold text-foreground">3. Labs fulfill the order</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Each order is routed to one of 12 certified optical labs across the EU and US. Lenses are cut, fitted, quality-checked, and shipped under your brand.
                    </p>
                  </li>
                  <li>
                    <h3 className="mb-1 text-base font-semibold text-foreground">4. GoHub handles support</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Returns, exchanges, prescription disputes, and lab issues are managed end-to-end by GoHub. You keep the customer relationship and the margin.
                    </p>
                  </li>
                </ol>
              </div>
            </section>
            <section className="bg-secondary/30 py-16 md:py-20" aria-labelledby="who-uses-gohub">
              <div className="container mx-auto max-w-4xl px-4 text-center">
                <h2 id="who-uses-gohub" className="mb-4 text-2xl font-bold text-foreground md:text-3xl">
                  Who uses GoHub
                </h2>
                <p className="mx-auto max-w-2xl text-muted-foreground">
                  Online pharmacies adding prescription eyewear as a high-margin category. Eyewear brands turning frame sales into complete prescription orders. Resellers consolidating supplier catalogs into one storefront. Independent opticians moving in-store expertise online without rebuilding their tech stack. Every segment runs on the same infrastructure: 17,000+ frames, 12 certified labs, and AI-guided checkout.
                </p>
              </div>
            </section>
            <BlogPreview />
            <FAQSection title={FAQS["/"].title} items={FAQS["/"].items} />
            <ContactForm />
            <Footer />
          </Suspense>
        </main>
      </div>
    </PageContentProvider>
  );
};

export default Index;
